/**
 * Lobby (DESIGN-client §7). Discord: the channel table is auto-created from the instance; seats
 * from the room state (participants fill the gaps before the room answers), Start for the host,
 * Invite. Web: Quick Play vs bots / Create private (TableOptions) / Join code. Bots are labeled;
 * tap an open or bot seat to swap into it.
 */
import { useEffect, useState } from 'react';
import { normalizeCode } from '@spades/shared';
import { fetchProfile } from '../../net/profileApi';
import { selectMyLevel } from '../../store/selectors';
import { store, useStore } from '../../store/store';
import { seatDisplayName, teamColorVar, teamOfSeat, copyText } from '../helpers';
import { codeFromSearch, inviteUrl, seatCardCopy } from '../lobby';
import { connectTable, intents, leaveToMenu, useRuntime } from '../runtime';
import { joinFailureText } from '../states';
import { OptionsState, defaultOptions, fromRules, rulesFromJson, summarize, toRulesInput } from '../tableOptions';
import { Avatar, toast } from '../ui';
import { LevelBadge } from '../ui/LevelBadge';
import { VolumeControl } from '../ui/VolumeControl';
import { EntryAtmosphere, EntryMenu } from './EntryMenu';
import { ProfileSheet } from '../sheets/Profile';
import { RulesSheet } from '../sheets/Rules';
import { TableOptionsSheet } from './TableOptions';
import { preloadTableArt } from './TableScreen';

/** Deep-link codes this page load has already tried; see the guard in the effect below. */
const attemptedCodes = new Set<string>();

/**
 * Which player-and-match this page load has already asked for a profile for, so a remount does not
 * ask twice.
 *
 * Module scope rather than a ref for the same reason `attemptedCodes` is: the lobby unmounts every
 * time the player enters a table and mounts again when they leave, and a per-mount guard would turn
 * each round trip into another request.
 *
 * Keyed by the last match as well as the player, because a record that only changes at match end
 * changes at MATCH END: a first-timer's very first request 404s, and a guard on the id alone would
 * leave them with no badge on the entry menu until they reloaded the page — precisely the player
 * the badge is meant to catch.
 */
let profileFetchedFor: string | null = null;

/**
 * Drawn rather than typed: ⚙ renders as a colour emoji in most fonts and would not match the UI.
 *
 * A toothed ring, not a hub with spokes — the spoked version reads as a sun at 20px, which is a
 * brightness control, not a settings one.
 */
function CogIcon() {
  return (
    <svg viewBox="0 0 24 24" width="21" height="21" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="3.1" />
      <path d="M19.2 14.4a1.5 1.5 0 0 0 .3 1.7l.1.1a1.8 1.8 0 1 1-2.6 2.6l-.1-.1a1.5 1.5 0 0 0-1.7-.3 1.5 1.5 0 0 0-.9 1.4v.2a1.8 1.8 0 1 1-3.6 0v-.1a1.5 1.5 0 0 0-1-1.4 1.5 1.5 0 0 0-1.7.3l-.1.1a1.8 1.8 0 1 1-2.6-2.6l.1-.1a1.5 1.5 0 0 0 .3-1.7 1.5 1.5 0 0 0-1.4-.9h-.2a1.8 1.8 0 1 1 0-3.6h.1a1.5 1.5 0 0 0 1.4-1 1.5 1.5 0 0 0-.3-1.7l-.1-.1a1.8 1.8 0 1 1 2.6-2.6l.1.1a1.5 1.5 0 0 0 1.7.3h.1a1.5 1.5 0 0 0 .9-1.4v-.2a1.8 1.8 0 1 1 3.6 0v.1a1.5 1.5 0 0 0 .9 1.4 1.5 1.5 0 0 0 1.7-.3l.1-.1a1.8 1.8 0 1 1 2.6 2.6l-.1.1a1.5 1.5 0 0 0-.3 1.7v.1a1.5 1.5 0 0 0 1.4.9h.2a1.8 1.8 0 1 1 0 3.6h-.1a1.5 1.5 0 0 0-1.4.9z" />
    </svg>
  );
}

export function Lobby() {
  const rt = useRuntime();
  const platform = useStore((s) => s.session.platform);
  const table = useStore((s) => s.table);
  const me = useStore((s) => s.session.me);
  const participants = useStore((s) => s.session.participants);
  const speaking = useStore((s) => s.session.speaking);
  const muted = useStore((s) => s.session.muted);
  const connection = useStore((s) => s.session.connection);
  const myLevel = useStore(selectMyLevel);
  /** The match whose XP the store is holding, if any — what re-arms the profile fetch below. */
  const lastMatchId = useStore((s) => s.progress.lastMatch?.matchId ?? null);
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [code, setCode] = useState('');
  const [joinOpen, setJoinOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);
  /**
   * Whose record the Profile sheet is showing, or null for closed.
   *
   * A user id rather than a boolean, because the same sheet serves both cases: the header chip
   * opens `'me'`, and tapping a seated player's card opens theirs. The server only hands over a
   * profile to someone sitting at the same table, so there is nothing here to guard.
   */
  const [profileOf, setProfileOf] = useState<string | null>(null);

  const isDiscord = platform === 'discord';
  const isHost = !!(me && table && table.hostPlayerId === me.id);
  const seated = !!(me && table?.seats.some((s) => s.playerId === me.id));
  const mySeat = me && table ? table.seats.findIndex((s) => s.playerId === me.id) : -1;

  // Warm the table's art while the player is deciding. The boot bundle is already in by now, so
  // this competes with nothing they are waiting on, and it is the difference between a quick play
  // that opens on a felt and one that opens on an empty canvas for two seconds.
  useEffect(() => {
    const warm = (): void => void preloadTableArt();
    const idle = (window as unknown as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number })
      .requestIdleCallback;
    if (idle) {
      const id = idle(warm, { timeout: 2000 });
      return () => (window as unknown as { cancelIdleCallback?: (h: number) => void }).cancelIdleCallback?.(id);
    }
    const t = window.setTimeout(warm, 600);
    return () => clearTimeout(t);
  }, []);

  /*
   * The badge on the identity pill: once per player, and again after each match they finish.
   *
   * Without this the entry menu has no level to draw — there is no table, so no `SeatMirror.level`
   * — and the badge would only appear after the player had opened their own record by hand, which
   * is backwards: the badge is the thing that makes them want to open it. Every failure is silent:
   * a guest with no finished match answers 404 and correctly gets no badge, and the match that
   * gives them one re-arms this by changing the key.
   */
  useEffect(() => {
    if (!me) return;
    const key = `${me.id}:${lastMatchId ?? ''}`;
    if (profileFetchedFor === key) return;
    profileFetchedFor = key;
    void fetchProfile('me').then((res) => {
      if (res.ok) store.getState().setProfile(res.profile);
    });
  }, [me, lastMatchId]);

  // Discord: the channel table is implicit; join as soon as the runtime is up.
  useEffect(() => {
    if (!rt || !isDiscord || connection !== 'idle') return;
    void connectTable({});
  }, [rt, isDiscord, connection]);

  // Web deep link: ?join=XXXXX (or the older ?code=) joins straight away. A malformed code never
  // reaches the server; the player lands in the lobby with an explanation instead of a spinner.
  useEffect(() => {
    if (!rt || isDiscord || connection !== 'idle') return;
    const p = new URLSearchParams(location.search);
    const raw = p.get('join') ?? p.get('code');
    // One attempt per code per page load. This effect re-runs whenever the connection state moves,
    // so once the player leaves a refused code the same `?join=` would fire again and trap them in
    // a loop they cannot leave. Module scope rather than a ref, so a remount does not forget it;
    // a genuine reload still gets a fresh try, which is what a reload should mean.
    if (!raw || attemptedCodes.has(raw)) return;
    attemptedCodes.add(raw);
    const norm = codeFromSearch(location.search);
    if (!norm) {
      toast(joinFailureText(raw, 'malformed'), 'error', 3600);
      return;
    }
    void connectTable({ code: norm });
  }, [rt, isDiscord, connection]);

  const run = async (fn: () => Promise<boolean>) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  /**
   * Start game: make a table with House Rules and put the player in it as host.
   *
   * Deliberately not `quickPlay: true` any more. That asked the server for a solo table, which arms
   * its own three-second auto-start (`TableRoom.SOLO_AUTOSTART_MS`) and deals the hand out from
   * under a host who wanted to invite someone first. The host presses Start now, and bots fill
   * whatever seats are still open when they do.
   */
  const onQuickPlay = () => run(() => connectTable({ options: toRulesInput(defaultOptions()) }));
  const onCreate = (opts: OptionsState) => {
    setOptionsOpen(false);
    if (table && isHost) {
      intents()?.setRules(toRulesInput(opts));
      toast('Rules updated', 'success');
      return;
    }
    void run(() => connectTable({ options: toRulesInput(opts) }));
  };
  const onJoin = () => {
    const norm = normalizeCode(code);
    if (!norm) {
      toast(joinFailureText(code, 'malformed'), 'error', 3600);
      return;
    }
    void run(() => connectTable({ code: norm }));
  };
  const onInvite = async () => {
    const bridge = rt?.bridge;
    if (bridge?.inviteAvailable()) {
      try {
        await bridge.openInvite();
        return;
      } catch {
        /* fall through to copy */
      }
    }
    const url = table?.code ? inviteUrl(table.code) : location.href;
    toast((await copyText(url)) ? 'Invite link copied' : url, 'success');
  };

  /**
   * Move to a seat — including from one the player is already in.
   *
   * The server refuses `sit` outright while a session holds a seat (`TableRoom.onSit` throws
   * `seat_taken: already seated`), so switching sides used to be impossible from the UI: the only
   * route from blue to red was leaving the table and joining it again. Standing up first turns the
   * refusal into a swap. Both intents travel the same socket in order, so the room sees the vacate
   * before the request.
   */
  const takeSeat = (i: 0 | 1 | 2 | 3): void => {
    const api = intents();
    if (!api) return;
    if (mySeat >= 0 && mySeat !== i) api.standUp();
    api.sit(i);
  };

  const rules = table ? fromRules(rulesFromJson(table.rulesJson)) : defaultOptions();
  const showTable = !!table;

  /*
   * No auto-start. The host presses Start.
   *
   * There used to be a ten-second countdown that dealt the hand by itself. It meant a host who
   * opened a table to invite a friend watched it start without them, and it gave everyone else a
   * clock they had not asked for. Nothing is lost by waiting: bots fill whatever seats are still
   * open at the moment Start is pressed, so a player on their own is one tap from a hand.
   */
  const startNow = () => {
    setStarted(true);
    intents()?.start();
  };

  /*
   * The web entry menu is its own screen (`EntryMenu.tsx`), not another `.screen__inner` column.
   *
   * It is the one surface that decides whether somebody plays at all, so it wants the full
   * viewport — a room behind it, a lamp pool, a wordmark with air around it — and `.screen` exists
   * to centre 560px of controls. Every other lobby state, the seated table and the Discord wait,
   * stays in that column, where four seat cards belong.
   *
   * Handlers, the join code and the options sheet all still live here; the menu is presentation.
   */
  if (!isDiscord && !showTable) {
    return (
      <>
        <EntryMenu
          me={me}
          speaking={!!(me && speaking[me.id])}
          muted={!!(me && muted[me.id])}
          level={myLevel}
          busy={busy}
          ready={!!rt}
          joinOpen={joinOpen}
          code={code}
          onToggleJoin={() => setJoinOpen((v) => !v)}
          onCodeChange={setCode}
          onQuickPlay={() => void onQuickPlay()}
          onJoin={onJoin}
          onOpenOptions={() => setOptionsOpen(true)}
        />
        <TableOptionsSheet
          open={optionsOpen}
          submitLabel="Create table"
          onClose={() => setOptionsOpen(false)}
          onSubmit={onCreate}
        />
      </>
    );
  }

  /*
   * The seated table, in the entry menu's world.
   *
   * Same `entry` frame, same room behind it, same button materials — a player who presses Start on
   * the menu and lands somewhere built out of different parts has been shown two products. What is
   * different here is the header, which carries the table's identity instead of the logo, and the
   * seats, which are the content.
   */
  return (
    <div className="entry lobby" data-testid="screen-lobby">
      <EntryAtmosphere />

      <div className="entry__top lobby__top">
        <div className="lobby__ident">
          {/* A table you cannot leave is a trap: the only way to a different table was to reload
              the page. Web only — a Discord channel table is the room the player is already in,
              and there is no menu behind it to go back to. */}
          {showTable && !isDiscord && (
            <button type="button" className="lobby__back" aria-label="Leave table" data-testid="leave-table" onClick={leaveToMenu}>
              ←
            </button>
          )}
          <div className="lobby__ident-text">
            <div className="lobby__eyebrow">{isDiscord ? 'Channel table' : table?.code ? `Table ${table.code}` : 'Your table'}</div>
            {showTable && <div className="lobby__rules">{summarize(rules)}</div>}
          </div>
        </div>
        <div className="entry__corner">
          <VolumeControl />
          {me && (
            /* The pill is the way into your own record. It was already the only thing on screen
               carrying your name and face, so it is where a player looks for themselves; a
               separate "Profile" button beside it would be a second answer to one question. */
            <button
              type="button"
              className="entry__me entry__me--button"
              title={me.name}
              aria-label="Your record"
              data-testid="open-profile"
              onClick={() => setProfileOf('me')}
            >
              <Avatar name={me.name} src={me.avatar} size={30} speaking={!!speaking[me.id]} muted={!!muted[me.id]} />
              <span className="entry__me-name">{me.name}</span>
              <LevelBadge level={myLevel} size={20} glow />
            </button>
          )}
        </div>
      </div>

      <div className="entry__content lobby__content">
        {(isDiscord || showTable) && (
          <div className="lobby__seats" data-testid="seats">
            {[0, 1, 2, 3].map((i) => {
              const seat = table?.seats[i];
              const participant = !table ? participants[i] : undefined;
              const name = seat ? seatDisplayName(seat) : participant?.displayName ?? 'Open seat';
              const empty = seat ? seat.controller === 'empty' : !participant;
              const bot = seat?.controller === 'bot';
              const mine = !!(me && seat?.playerId === me.id);
              const pid = seat?.playerId || participant?.id || '';
              const team = teamOfSeat(i);
              const copy = seatCardCopy({
                controller: seat?.controller ?? 'empty',
                connected: seat?.connected ?? true,
                mine,
                team,
                myTeam: mySeat < 0 ? null : teamOfSeat(mySeat),
                phase: table?.phase ?? 'lobby',
              });
              const canTake = !!table && copy.canTake;
              // A chair you cannot sit in still belongs to somebody, and their record is the one
              // thing worth knowing about them before a hand. Only for a seated human: a bot has
              // no record and an open seat has nobody. Sitting always wins the tap when both apply.
              const profileId = !canTake && seat?.controller === 'human' && seat.playerId ? seat.playerId : null;
              const act = canTake ? () => takeSeat(i as 0 | 1 | 2 | 3) : profileId ? () => setProfileOf(profileId) : null;
              return (
                <div
                  key={i}
                  className={`seat${empty ? ' seat--empty' : ''}${mine ? ' seat--mine' : ''}${canTake ? ' seat--open' : ''}`}
                  style={{ ['--team-color' as string]: teamColorVar(team) }}
                  data-testid={`seat-${i}`}
                  role={act ? 'button' : undefined}
                  tabIndex={act ? 0 : undefined}
                  aria-label={
                    canTake
                      ? empty
                        ? `Sit in seat ${i + 1}`
                        : `Take seat ${i + 1} from the bot`
                      : profileId
                        ? `${name}'s record`
                        : undefined
                  }
                  onClick={() => act?.()}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && act?.()}
                >
                  {/* The team colour as a spine down the edge of the plaque, not as the plaque.
                      Four cards in two flat team colours read as a scoreboard; a walnut plaque with
                      a coloured edge reads as a chair that belongs to a side. */}
                  <span className="seat__spine" aria-hidden />
                  <Avatar name={empty ? '+' : name} src={seat?.avatar || participant?.avatarUrl} size={36} team={team} bot={bot} speaking={!!speaking[pid]} muted={!!muted[pid]} />
                  <div className="seat__text">
                    {/* A seat card is a chair, never an invite. It used to open the invite
                        dialog, so a player who wanted to move from blue to red got a share
                        sheet instead; the only way in was to leave the table. Invite has its
                        own button below the seats now, and this one seats you. */}
                    <div className="seat__name">
                      <span className="seat__name-text">{empty ? 'Open seat' : name}</span>
                      {/* 0 until the server has a finished match for this seat, and the badge
                          renders nothing for 0 — so an unranked player simply has no badge. */}
                      <LevelBadge level={seat?.level ?? null} size={17} className="seat__level" />
                    </div>
                    <div className="seat__tag">{table ? copy.tag : participant ? 'In the call' : 'Open seat'}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showTable ? (
          <div className="lobby__actions">
            {isHost ? (
              <>
                {/* Said before the button, not after it: the question a host hesitates over is
                    whether starting with empty seats means playing three-handed. */}
                <p className="entry__note" data-testid="bots-note">
                  Empty seats are filled with bots when you start.
                </p>
                {/* `started` latches so a double tap cannot send two `start` intents while the
                    room is still moving out of the lobby. */}
                <button
                  type="button"
                  className="cta cta--primary"
                  data-testid="start"
                  disabled={connection !== 'connected' || started}
                  onClick={startNow}
                >
                  <span className="cta__glyph" aria-hidden>
                    ♠
                  </span>
                  <span className="cta__label">Start the game</span>
                </button>
              </>
            ) : (
              <p className="lobby__waiting" data-testid="waiting">
                {seated ? 'Waiting for the host to start…' : 'Tap an open seat to sit'}
              </p>
            )}
            {/* Invite takes the width because it is the only thing a waiting table is for; the
                rules are the cog beside it. Two text buttons of equal weight read as a choice
                between inviting someone and reading the rules, which is not the choice a host has
                at this moment. */}
            <div className="lobby__row">
              <button type="button" className="cta cta--join" data-testid="invite" onClick={() => void onInvite()}>
                <span className="cta__label">Invite</span>
              </button>
              {isHost && (
                <button type="button" className="cta cta--join cta--icon" aria-label="Table rules" data-testid="rules-edit" onClick={() => setOptionsOpen(true)}>
                  <CogIcon />
                </button>
              )}
            </div>
            {seated && !isHost && (
              <button type="button" className="entry__foot-link lobby__stand" onClick={() => intents()?.standUp()}>
                Stand up
              </button>
            )}
          </div>
        ) : (
          /* Not `isDiscord ?` any more: the web menu returned above, so the only way to be here
             without a table is the Discord channel table still being set up. */
          <div className="lobby__actions" data-testid="waiting">
            {connection === 'lost' ? (
              // The auto-join effect only fires from `idle`, and a failed connect leaves `lost`,
              // so nothing was retrying — the old copy claimed a retry that never came and the
              // Discord branch offers no other control, leaving the player stuck until a reload.
              <>
                <p className="lobby__waiting">Can&apos;t reach the table server.</p>
                <button type="button" className="cta cta--join" data-testid="retry-connect" disabled={busy} onClick={() => void run(() => connectTable({}))}>
                  <span className="cta__label">Try again</span>
                </button>
              </>
            ) : (
              <p className="lobby__waiting">Setting up the channel table…</p>
            )}
          </div>
        )}
      </div>

      <div className="entry__foot">
        <button type="button" className="entry__foot-link" data-testid="how-to-play" onClick={() => setRulesOpen(true)}>
          How to play
        </button>
      </div>

      <RulesSheet open={rulesOpen} onClose={() => setRulesOpen(false)} />

      <ProfileSheet open={profileOf != null} userId={profileOf ?? 'me'} onClose={() => setProfileOf(null)} />

      <TableOptionsSheet
        open={optionsOpen}
        initial={table && isHost ? rules : undefined}
        submitLabel={table && isHost ? 'Save rules' : 'Create table'}
        onClose={() => setOptionsOpen(false)}
        onSubmit={onCreate}
      />
    </div>
  );
}
